/**
 * KnowledgeTab — Published knowledge gallery on the user profile page.
 *
 * Displays structured knowledge content: professional news, scholarly articles,
 * how-to guides, best practices, and lessons learned. Personal blogs and
 * opinion pieces have been moved to the BlogTab per Content Architecture Spec §3.2.
 *
 * Data sources:
 *   1. API: useApiUserKnowledge → GET /api/blog/articles?AuthorGuid={guid}&Status=published
 *   2. localStorage fallback: reads published drafts from local storage
 *      until Phase 2 server persistence is wired up.
 *
 * Follows the MyWorkspacesTab pattern: separate component + CSS module,
 * props: { userGuid, isOwnProfile }, uses useAppTranslation('userProfile').
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiUserKnowledge } from '../../api/useApiUserKnowledge';
import { mediaSrc } from '../../api/mediaUrl';
import { BLOG_TYPES } from '../../Data/Blog/blogTypes';
import type { BlogDraft } from '../../Data/Blog/blogDraftStore';
import styles from './KnowledgeTab.module.css';

// Blog types that have moved to BlogTab (personal expression)
// Knowledge tab only shows structured knowledge content.
const BLOG_TYPE_KEYS_EXCLUDED = new Set(['opinion', 'personal']);

// ── localStorage reader (Phase 2: remove once server persistence is live) ──

const STORAGE_KEY = 'l4e_blog_drafts';

function loadPublishedDrafts(): BlogDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as BlogDraft[];
    return all.filter(d => d.status === 'published' && !BLOG_TYPE_KEYS_EXCLUDED.has(d.blogType));
  } catch {
    return [];
  }
}

// ── Props ───────────────────────────────────────────────────────

interface Props {
  userGuid: string;
  isOwnProfile: boolean;
  /** Display name — retained for API compat but not used in gallery */
  displayName?: string;
}

// ── Component ───────────────────────────────────────────────────

export default function KnowledgeTab({ userGuid, isOwnProfile }: Props) {
  const { t } = useAppTranslation('userProfile');
  const navigate = useNavigate();

  // API-sourced published articles — exclude blog types (now on BlogTab)
  const { articles: rawArticles, loading } = useApiUserKnowledge(userGuid);
  const articles = useMemo(
    () => rawArticles.filter(a => !BLOG_TYPE_KEYS_EXCLUDED.has(a.blogType || '')),
    [rawArticles]
  );

  // localStorage-sourced published drafts (Phase 2: remove)
  const localPublished = useMemo(() => loadPublishedDrafts(), []);

  // Merge: API articles take priority; local drafts fill gaps while
  // server persistence isn't wired. Deduplicate by headline as a rough key.
  const apiTitles = useMemo(
    () => new Set(articles.map(a => (a.title || '').toUpperCase())),
    [articles]
  );

  const allPublished = useMemo(() => {
    // Map local drafts into a display-compatible shape
    const localCards = localPublished
      .filter(d => !apiTitles.has((d.headline || '').toUpperCase()))
      .map(d => ({
        key: d.draftId,
        title: d.headline || t('knowledge.untitledDraft'),
        blogType: d.blogType,
        excerpt: d.bodyText.slice(0, 140),
        wordCount: d.wordCount,
        heroImageUrl: d.heroImageUrl || null,
        date: d.updatedAt,
        source: 'local' as const,
      }));

    const apiCards = articles.map(a => ({
      key: a.articleGuid,
      title: a.title,
      blogType: (a.blogType || 'personal') as keyof typeof BLOG_TYPES,
      excerpt: a.excerpt || '',
      wordCount: a.readingTimeMin ? a.readingTimeMin * 200 : 0,
      heroImageUrl: a.featuredImageUrl || null,
      date: a.publishedAt || a.createdAt,
      source: 'api' as const,
    }));

    return [...apiCards, ...localCards];
  }, [articles, localPublished, apiTitles, t]);

  // ── Loading state ──

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>{t('knowledge.title')}</h3>
            <p className={styles.subtitle}>{t('knowledge.profileSubtitle')}</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p className={styles.emptyDesc}>{t('knowledge.loading')}</p>
        </div>
      </div>
    );
  }

  // ── Published gallery ──

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>{t('knowledge.title')}</h3>
          <p className={styles.subtitle}>{t('knowledge.profileSubtitle')}</p>
        </div>
        {isOwnProfile && (
          <button
            type="button"
            className={styles.writeBtn}
            onClick={() => navigate('/knowledge')}
          >
            {t('knowledge.manageButton')}
          </button>
        )}
      </div>

      {/* Published article cards */}
      {allPublished.length > 0 && (
        <div className={styles.section}>
          <div className={styles.cardGrid}>
            {allPublished.map(item => {
              const typeDef = BLOG_TYPES[item.blogType] || BLOG_TYPES.personal;
              return (
                <div
                  key={item.key}
                  className={item.heroImageUrl ? styles.draftCardWithHero : styles.draftCard}
                >
                  {item.heroImageUrl && (
                    <div className={styles.draftHeroWrap}>
                      <img src={mediaSrc(item.heroImageUrl) ?? undefined} alt="" className={styles.draftHero} />
                    </div>
                  )}
                  <div className={styles.draftCardBody}>
                    <div className={styles.draftCardHeader}>
                      <span className={item.blogType === 'opinion' ? styles.draftBadgeOpinion : styles.draftBadge}>
                        {typeDef.icon} {t(`knowledge.blogTypes.${item.blogType}.name`)}
                      </span>
                    </div>
                    <h4 className={styles.draftTitle}>{item.title}</h4>
                    {item.excerpt && (
                      <p className={styles.draftExcerpt}>
                        {item.excerpt.slice(0, 120)}{item.excerpt.length > 120 ? '…' : ''}
                      </p>
                    )}
                    <div className={styles.draftMeta}>
                      {item.wordCount > 0 && (
                        <span>{item.wordCount} {t('knowledge.editor.words')}</span>
                      )}
                      <span>{new Date(item.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {allPublished.length === 0 && (
        <div className={styles.emptyState}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-muted)' }}>
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
          <h3 className={styles.emptyTitle}>
            {isOwnProfile ? t('knowledge.emptyOwn') : t('knowledge.emptyOther')}
          </h3>
          <p className={styles.emptyDesc}>
            {isOwnProfile ? t('knowledge.emptyOwnDesc') : t('knowledge.emptyOtherDesc')}
          </p>
        </div>
      )}
    </div>
  );
}
